import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import type { PhoneNumberWithCountryCode } from '@gov-design-system-ce/react-form';
import { phoneNumberSchema } from '@gov-design-system-ce/react-form';
import { LocalizeNameSpaceTypes } from '@gov-nx/core/service';
import { compose } from '@gov-nx/core/types';
import type { FormPhoneNumberProps } from '@gov-nx/ui/types';
import { digitsOnly, isCzechFormat, phoneValidator } from './Validator/phone';

interface PhoneShape {
	isRequired?: boolean;
	isRequiredMessage?: string;
	isInvalidMessage?: string;
}

export const usePhoneShape = () => {
	const { t } = useTranslation([LocalizeNameSpaceTypes.Form]);

	return {
		getPhoneShape: (props?: PhoneShape): yup.StringSchema => {
			const schema = yup
				.string()
				.test(
					'length',
					props?.isInvalidMessage ??
						t('telefon.validace.telefon-neni-ve-spravnem-tvaru'),
					phoneValidator
				);

			if (props?.isRequired) {
				return schema.required(
					props?.isRequiredMessage ?? t('telefon.validace.zadejte-telefon')
				);
			}

			return schema;
		},
	};
};

export const getDefaultPhoneValue = (): PhoneNumberWithCountryCode => ({
	countryCode: '420',
	number: '',
});

type PhoneShapeOptions = {
	isRequiredWhen?: { fieldName: string; is: boolean };
	isRequired?: boolean;
};

export const usePhoneShapeWithCountryCodes = () => {
	const { t } = useTranslation([LocalizeNameSpaceTypes.Form]);

	function getPhoneShape(options: PhoneShapeOptions = {}) {
		return phoneNumberSchema({
			isRequiredWhen: options.isRequiredWhen
				? {
						...options.isRequiredWhen,
						message: t('telefon.validace.zadejte-telefon'),
					}
				: undefined,
			isRequired: options?.isRequired
				? { message: t('telefon.validace.zadejte-telefon') }
				: undefined,
			errorMessages: {
				wrongFormat: t('telefon.validace.telefon-neni-ve-spravnem-tvaru'),
			},
		});
	}

	return {
		getPhoneShape,
		getDefaultValue: getDefaultPhoneValue,
	};
};

export const formatPhoneNumber = (phoneNumber: string): string => {
	if (isCzechFormat(phoneNumber)) {
		const cleanedNumber = digitsOnly(phoneNumber);
		if (phoneNumber.startsWith('+420')) {
			return cleanedNumber.replace(
				/(\d{3})(\d{3})(\d{3})(\d{3})/,
				'+$1 $2 $3 $4'
			);
		}
		if (phoneNumber.startsWith('00420')) {
			return cleanedNumber.replace(
				/(\d{5})(\d{3})(\d{3})(\d{3})/,
				'$1 $2 $3 $4'
			);
		}
		return cleanedNumber.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
	}

	return phoneNumber;
};

export const phoneShapeToString = (phone: PhoneNumberWithCountryCode): string =>
	['+', phone.countryCode, phone.number].join('');

export const phoneShapeReadable: (phone: PhoneNumberWithCountryCode) => string =
	compose(formatPhoneNumber, phoneShapeToString);

export interface PhoneDefinition {
	defaultValue: PhoneNumberWithCountryCode;
	field: FormPhoneNumberProps;
	schema: yup.ObjectSchema<PhoneNumberWithCountryCode>;
}

interface FieldProps extends PhoneShapeOptions {
	name: string;
	label: string;
	placeholder?: string;
	isDisabled?: boolean;
}

export const usePhoneDefinition = (props: FieldProps): PhoneDefinition => {
	const { getPhoneShape, getDefaultValue } = usePhoneShapeWithCountryCodes();

	return {
		defaultValue: getDefaultValue(),
		schema: getPhoneShape(props),
		field: {
			field: {
				name: props.name,
				placeholder: props.placeholder,
				isDisabled: props.isDisabled,
			},
			label: { children: props.label },
		},
	};
};
